import type { WeekdayNumbers } from "luxon";

export const REPORT_MAIN_CATEGORY_IDS = {
    PRP: "prp",
    SALES_SUMMARIES: "sales-summaries",
    SETTING_CAPACITIES: "setting-capacities",
    CALENDAR_CAPACITIES: "calendar-capacities",
    TEAM_PERFORMANCE: "team-performance",
    CALL_VOLUME: "call-volume",
    REACHABILITY_ANALYSIS: "reachability-analysis",
} as const;

export type ReportMainCategoryId = (typeof REPORT_MAIN_CATEGORY_IDS)[keyof typeof REPORT_MAIN_CATEGORY_IDS];

export const SALES_SUMMARY_PERIOD_IDS = {
    DAILY: "daily",
    WEEKLY: "weekly",
    MONTHLY: "monthly",
} as const;

export const SALES_SUMMARY_PERIOD_ORDER = [
    SALES_SUMMARY_PERIOD_IDS.DAILY,
    SALES_SUMMARY_PERIOD_IDS.WEEKLY,
    SALES_SUMMARY_PERIOD_IDS.MONTHLY,
] as const;

export type SalesSummaryPeriodId = (typeof SALES_SUMMARY_PERIOD_IDS)[keyof typeof SALES_SUMMARY_PERIOD_IDS];

/** Zoho-style dashboard names shown in the top-left selector. */
export const REPORT_DASHBOARD_IDS = {
    SALES_DACH: "sales-dach",
    SALES_PRP: "sales-prp",
    SETTING_CAPACITIES: "setting-capacities",
    CALENDAR_CAPACITIES: "calendar-capacities",
    TEAM_PERFORMANCE: "team-performance",
    CALL_VOLUME: "call-volume",
    REACHABILITY_ANALYSIS: "reachability-analysis",
} as const;

export type ReportDashboardId = (typeof REPORT_DASHBOARD_IDS)[keyof typeof REPORT_DASHBOARD_IDS];

export const REPORT_SELECT_GROUP_IDS = {
    SALES_SUMMARIES: "sales-summaries",
    CAPACITIES: "capacities",
    PERFORMANCE: "performance",
} as const;

export type ReportSelectGroupId = (typeof REPORT_SELECT_GROUP_IDS)[keyof typeof REPORT_SELECT_GROUP_IDS];

export const REPORT_SELECT_GROUPS: { id: ReportSelectGroupId; labelKey: string }[] = [
    {
        id: REPORT_SELECT_GROUP_IDS.SALES_SUMMARIES,
        labelKey: "performance_reports_group_sales_summaries",
    },
    {
        id: REPORT_SELECT_GROUP_IDS.CAPACITIES,
        labelKey: "performance_reports_group_capacities",
    },
    {
        id: REPORT_SELECT_GROUP_IDS.PERFORMANCE,
        labelKey: "performance",
    },
];

export type ReportDashboardOption = {
    id: ReportDashboardId;
    category: ReportMainCategoryId;
    group: ReportSelectGroupId;
    labelKey: string;
};

export const REPORT_DASHBOARD_OPTIONS: ReportDashboardOption[] = [
    {
        id: REPORT_DASHBOARD_IDS.SALES_DACH,
        category: REPORT_MAIN_CATEGORY_IDS.SALES_SUMMARIES,
        group: REPORT_SELECT_GROUP_IDS.SALES_SUMMARIES,
        labelKey: "performance_reports_dashboard_sales_dach",
    },
    {
        id: REPORT_DASHBOARD_IDS.SALES_PRP,
        category: REPORT_MAIN_CATEGORY_IDS.PRP,
        group: REPORT_SELECT_GROUP_IDS.SALES_SUMMARIES,
        labelKey: "performance_reports_dashboard_sales_prp",
    },
    {
        id: REPORT_DASHBOARD_IDS.SETTING_CAPACITIES,
        category: REPORT_MAIN_CATEGORY_IDS.SETTING_CAPACITIES,
        group: REPORT_SELECT_GROUP_IDS.CAPACITIES,
        labelKey: "setting_capacities",
    },
    {
        id: REPORT_DASHBOARD_IDS.CALENDAR_CAPACITIES,
        category: REPORT_MAIN_CATEGORY_IDS.CALENDAR_CAPACITIES,
        group: REPORT_SELECT_GROUP_IDS.CAPACITIES,
        labelKey: "calendar_capacities_title",
    },
    {
        id: REPORT_DASHBOARD_IDS.TEAM_PERFORMANCE,
        category: REPORT_MAIN_CATEGORY_IDS.TEAM_PERFORMANCE,
        group: REPORT_SELECT_GROUP_IDS.PERFORMANCE,
        labelKey: "team_performance",
    },
    {
        id: REPORT_DASHBOARD_IDS.CALL_VOLUME,
        category: REPORT_MAIN_CATEGORY_IDS.CALL_VOLUME,
        group: REPORT_SELECT_GROUP_IDS.PERFORMANCE,
        labelKey: "call_volume",
    },
    {
        id: REPORT_DASHBOARD_IDS.REACHABILITY_ANALYSIS,
        category: REPORT_MAIN_CATEGORY_IDS.REACHABILITY_ANALYSIS,
        group: REPORT_SELECT_GROUP_IDS.PERFORMANCE,
        labelKey: "reachability_analysis",
    },
];

export const SALES_SUMMARY_PERIOD_LABEL_KEYS = {
    daily: "performance_reports_category_daily_summary",
    weekly: "performance_reports_category_weekly_summary",
    monthly: "performance_reports_category_monthly_summary",
} as const satisfies Record<SalesSummaryPeriodId, string>;

export const REPORT_VIEW_IDS = {
    FIRST_HALF: "first_half",
    SECOND_HALF: "second_half",
    DAILY: "daily",
    WEEKLY: "weekly",
} as const;

export type ReportViewId = (typeof REPORT_VIEW_IDS)[keyof typeof REPORT_VIEW_IDS];

export type ReportPillVariant = "good" | "warn";

export type ReportItemTone = "green" | "orange";

export type ReportPill = {
    text?: string;
    variant: ReportPillVariant;
};

export type ReportItem = {
    text?: string;
    tone: ReportItemTone;
};

export type ReportFocus = {
    labelKey: string;
    label?: string;
    text?: string;
};

export type ReportSummary = {
    score: number;
    titleKey: string;
    title?: string;
    subtitleKey: string;
    subtitle?: string;
    subtitleCount?: number;
    pills: ReportPill[];
};

export type ReportSections = {
    strengthsTitleKey: string;
    weaknessesTitleKey: string;
    focusTitleKey: string;
    focus: ReportFocus;
    conclusionTitleKey: string;
    conclusion?: string;
    strengths: ReportItem[];
    weaknesses: ReportItem[];
};

export type HalfCompareCard = {
    labelKey: string;
    label?: string;
    score: number;
    callsCount: number;
    timeRangeKey: string;
    timeRange?: string;
    accent?: "default" | "positive";
};

export type WeekDayReport = {
    weekdayIndex: WeekdayNumbers;
    score: number | null;
    callsCount: number | null;
    isToday?: boolean;
    isPending?: boolean;
};

export type WeeklyComparison = {
    previousWeekAverage: number;
    currentWeekAverage: number;
    trendText?: string;
    trendDirection: "up" | "down" | "stable";
};
